import { DatePipe } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  output,
  signal,
} from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { CertificateRecord, PublicEvent } from "../transparency-log";
import { RULES, WatchFinding } from "../watch-rules";
import {
  WatchContext,
  describeEvent,
  eventLink,
  matchesText,
  wrpLink,
} from "../watch-view-model";
import { CertificateInspector } from "./certificate-dialog.component";

export type SeverityFilter = "all" | "high" | "warning" | "info";

@Component({
  selector: "watch-findings-view",
  imports: [DatePipe, MatIconModule],
  templateUrl: "./findings-view.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FindingsViewComponent {
  private readonly inspector = inject(CertificateInspector);

  readonly findings = input.required<WatchFinding[]>();
  readonly context = input.required<WatchContext>();
  readonly normalCount = input(0);
  readonly selectedId = model<string | undefined>();
  readonly severity = model<SeverityFilter>("all");
  readonly openWrp = output<string>();

  readonly rules = RULES;
  readonly query = signal("");

  readonly counts = computed(() => {
    const all = this.findings();
    return {
      all: all.length,
      high: all.filter((finding) => finding.severity === "high").length,
      warning: all.filter((finding) => finding.severity === "warning").length,
      info: all.filter((finding) => finding.severity === "info").length,
    };
  });

  readonly visible = computed(() =>
    this.findings().filter(
      (finding) =>
        (this.severity() === "all" || finding.severity === this.severity()) &&
        matchesText(
          this.query(),
          finding.title,
          finding.rule,
          finding.wrpId,
          this.name(finding.wrpId),
        ),
    ),
  );

  readonly selected = computed<WatchFinding | undefined>(
    () =>
      this.visible().find((finding) => finding.id === this.selectedId()) ??
      this.visible()[0],
  );

  readonly filters: Array<{ value: SeverityFilter; label: string }> = [
    { value: "all", label: "All" },
    { value: "high", label: "High" },
    { value: "warning", label: "Warning" },
    { value: "info", label: "Data quality" },
  ];

  /** Findings about the log as a whole (R-13) name no WRP. */
  name(wrpId: string): string {
    return wrpId ? (this.context().names.get(wrpId) ?? wrpId) : "Event log";
  }

  describe(event: PublicEvent) {
    return describeEvent(this.context(), event);
  }

  eventLink(event: PublicEvent): string {
    return eventLink(this.context(), event);
  }

  wrpLink(wrpId: string): string {
    return wrpLink(this.context(), wrpId);
  }

  inspect(record: CertificateRecord): void {
    this.inspector.open(record, this.context());
  }

  onQuery(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
  }
}
